import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { signOut, setUpTOTP } from "@aws-amplify/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";

// Use Auth from aws-amplify for authenticated operations

import "./amplify-config.js";

import SidebarWithContent from "./ui-components/SideBar.js";

/* ------------------ Constants ------------------ */
const AUTO_LOGOUT_MS = 10 * 60 * 1000; // 10 minutes inactivity
const POPUP_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes to respond
const AUTO_LOGOUT_STATE_KEY = "whapp.autoLogoutState";
const states = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

/* ------------------ Auto Logout Hook ------------------ */
export function useAutoLogout() {
  const timeoutRef = useRef(null);
  const popupTimeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const passiveViewingIntervalRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const inactivityDeadlineRef = useRef(null);
  const popupDeadlineRef = useRef(null);
  const [showPopup, setShowPopup] = useState(false);

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (passiveViewingIntervalRef.current)
      clearInterval(passiveViewingIntervalRef.current);
  }, []);

  const signOutUser = useCallback(async () => {
    try {
      console.log(
        "⌛️ Signing out user due to inactivity at",
        new Date().toLocaleTimeString(),
      );
      await signOut();
      sessionStorage.removeItem(AUTO_LOGOUT_STATE_KEY);
      window.location.href = "/";
    } catch (error) {
      console.error("Error signing out due to inactivity:", error);
    }
  }, []);

  const onInactivity = useCallback(() => {
    console.log(
      "⌛️ Inactivity timeout reached — showing popup at",
      new Date().toLocaleTimeString(),
    );
    setShowPopup(true);
    const popupDeadline = Date.now() + POPUP_TIMEOUT_MS;
    popupDeadlineRef.current = popupDeadline;
    sessionStorage.setItem(
      AUTO_LOGOUT_STATE_KEY,
      JSON.stringify({ phase: "warning", deadline: popupDeadline }),
    );

    popupTimeoutRef.current = setTimeout(() => {
      signOutUser();
    }, POPUP_TIMEOUT_MS);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, [signOutUser]);

  const resetTimeout = useCallback(() => {
    clearAllTimers();
    setShowPopup(false);
    startTimeRef.current = Date.now();
    const inactivityDeadline = startTimeRef.current + AUTO_LOGOUT_MS;
    inactivityDeadlineRef.current = inactivityDeadline;
    popupDeadlineRef.current = null;
    sessionStorage.setItem(
      AUTO_LOGOUT_STATE_KEY,
      JSON.stringify({ phase: "idle", deadline: inactivityDeadline }),
    );
    console.log("⏳ Timeout reset at", new Date().toLocaleTimeString());

    timeoutRef.current = setTimeout(onInactivity, AUTO_LOGOUT_MS);

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(AUTO_LOGOUT_MS - elapsed, 0);
      console.log(
        `⏲️ Remaining time before popup: ${Math.floor(remaining / 1000)}s`,
      );
    }, 5000);

    // If page has iframes (e.g., embedded videos), periodically reset timeout
    // to keep user logged in during passive viewing
    const iframes = document.querySelectorAll("iframe");
    if (iframes.length > 0 && document.hasFocus()) {
      if (passiveViewingIntervalRef.current) {
        clearInterval(passiveViewingIntervalRef.current);
      }
      passiveViewingIntervalRef.current = setInterval(
        () => {
          // Every 5 minutes while on a page with iframes and window is focused,
          // extend the session to handle passive video watching
          if (document.hasFocus()) {
            console.log("⏳ Extending timeout for passive iframe viewing");
            startTimeRef.current = Date.now();
          }
        },
        5 * 60 * 1000,
      ); // Reset every 5 minutes if window is focused
    }
  }, [clearAllTimers, onInactivity]);

  const resumeTimeout = useCallback(() => {
    let savedState = null;
    try {
      savedState = JSON.parse(sessionStorage.getItem(AUTO_LOGOUT_STATE_KEY));
    } catch {}

    const deadline = Number(savedState?.deadline);
    if (!deadline || deadline <= 0) {
      resetTimeout();
      return;
    }

    const remaining = deadline - Date.now();
    clearAllTimers();

    if (savedState.phase === "warning") {
      popupDeadlineRef.current = deadline;
      setShowPopup(true);
      if (remaining <= 0) {
        signOutUser();
      } else {
        popupTimeoutRef.current = setTimeout(signOutUser, remaining);
      }
      return;
    }

    if (remaining <= 0) {
      onInactivity();
      return;
    }

    inactivityDeadlineRef.current = deadline;
    popupDeadlineRef.current = null;
    startTimeRef.current = deadline - AUTO_LOGOUT_MS;
    setShowPopup(false);
    timeoutRef.current = setTimeout(onInactivity, remaining);
  }, [clearAllTimers, onInactivity, resetTimeout, signOutUser]);

  const handleStillHere = () => {
    console.log(
      "✅ User confirmed they are still here at",
      new Date().toLocaleTimeString(),
    );
    resetTimeout();
  };

  useEffect(() => {
    const events = [
      "mousemove",
      "keydown",
      "mousedown",
      "scroll",
      "touchstart",
    ];

    const handleMouseMove = (e) => {
      // Check if mouse moved over any iframe
      const iframes = document.querySelectorAll("iframe");
      for (let iframe of iframes) {
        const rect = iframe.getBoundingClientRect();
        if (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom
        ) {
          // Mouse is over an iframe - treat as activity
          resetTimeout();
          return;
        }
      }
      // Also handle regular mouse move
      resetTimeout();
    };

    events.forEach((e) => {
      if (e === "mousemove") {
        window.addEventListener(e, handleMouseMove);
      } else {
        window.addEventListener(e, resetTimeout);
      }
    });

    // Track iframe focus for keyboard/click interactions
    const handleIframeFocus = (e) => {
      if (e.target.tagName === "IFRAME") {
        resetTimeout();
      }
    };

    window.addEventListener("focus", handleIframeFocus);

    resumeTimeout();

    const handlePageResume = () => {
      if (document.visibilityState === "visible") resumeTimeout();
    };
    document.addEventListener("visibilitychange", handlePageResume);
    window.addEventListener("pageshow", handlePageResume);
    window.addEventListener("focus", handlePageResume);

    return () => {
      events.forEach((e) => {
        if (e === "mousemove") {
          window.removeEventListener(e, handleMouseMove);
        } else {
          window.removeEventListener(e, resetTimeout);
        }
      });
      window.removeEventListener("focus", handleIframeFocus);
      document.removeEventListener("visibilitychange", handlePageResume);
      window.removeEventListener("pageshow", handlePageResume);
      window.removeEventListener("focus", handlePageResume);
      clearAllTimers();
    };
  }, [clearAllTimers, resetTimeout, resumeTimeout]);

  return { showPopup, handleStillHere };
}

/* ------------------ App ------------------ */
export default function App() {
  const { showPopup, handleStillHere } = useAutoLogout();
  const [accountType, setAccountType] = useState("Individual");
  const [secretKey, setSecretKey] = useState(null);

  function TOTPSetup({ user }) {
    const isSettingUpTOTPRef = useRef(false);

    useEffect(() => {
      const fetchTOTP = async () => {
        if (!user) return; // only run when user is available
        if (isSettingUpTOTPRef.current) return; // prevent concurrent calls
        isSettingUpTOTPRef.current = true;
        try {
          const { secretCode } = await setUpTOTP({ user });
          setSecretKey(secretCode);
        } catch (err) {
          console.error("Error fetching TOTP secret:", err);
        } finally {
          isSettingUpTOTPRef.current = false;
        }
      };

      fetchTOTP();
    }, [user]);

    return null;
  }
  // Load user attributes when user is set

  return (
    <div
      className="app-auth-shell"
      style={{ maxWidth: 400, margin: "auto", marginTop: 40 }}
    >
      {/* The inactivity popup modal */}

      <Authenticator
        initialState="signIn"
        formFields={{
          signIn: {
            username: {
              label: "Email",
              placeholder: "Enter your email",
            },
          },
          signUp: {
            username: {
              label: "Username",
              placeholder: "Enter your username",
            },
          },
        }}
        components={{
          SetupTOTP: ({ QRCode }) => (
            <div style={{ textAlign: "center" }}>
              <p>Scan this QR code with your authenticator app:</p>
              <img
                src={QRCode}
                alt="MFA QR Code"
                style={{
                  margin: "20px auto",
                  display: "block",
                  width: 200,
                  height: 200,
                }}
              />

              {secretKey && (
                <p style={{ marginTop: 16 }}>
                  Or enter this code manually: <strong>{secretKey}</strong>
                </p>
              )}

              <p style={{ marginTop: 16 }}>
                After scanning, enter the 6-digit code from your authenticator
                app to finish setup.
              </p>
              {/* 🚫 Copy button is gone */}
            </div>
          ),
          SignIn: {
            Header() {
              return (
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <img
                    src="/Wholistic-Depiction-Circle-App.png"
                    alt="Logo"
                    style={{ width: 250, height: "auto" }}
                  />
                </div>
              );
            },
            Footer() {
              return (
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 13,
                    color: "#374151",
                    textAlign: "left",
                  }}
                >
                  <p>
                    A digital health platform that enables users to receive
                    immediate results from evidence-based assessments, create
                    personalized health plans, track meaningful progress,
                    collaborate with health professionals, and access to
                    CoConnect™ social platform that encourages healthy habits.
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    You will need an authenticator app to complete your account
                    setup for protection. Some authenticator apps include
                    Google, Duo, and Authy. Keep your authenticator app because
                    you will need the code it provides for each sign in.{" "}
                    <strong>
                      If you lose access to your authenticator, you will need to
                      create a new account.
                    </strong>
                  </p>
                </div>
              );
            },
          },
          SignUp: {
            Header() {
              return (
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <img
                    src="/Wholistic-Depiction-Circle-App.png"
                    alt="Logo"
                    style={{ width: 250, height: "auto" }}
                  />
                </div>
              );
            },
            FormFields() {
              return (
                <>
                  <div className="amplify-field">
                    <label className="amplify-label">Account Type</label>
                    <select
                      name="custom:accountType"
                      value={accountType}
                      onChange={(e) => setAccountType(e.target.value)}
                      required
                      className="amplify-input"
                    >
                      <option value="Individual">Individual</option>
                      <option value="Professional">Professional</option>
                      <option value="Organization">Organization</option>
                    </select>
                  </div>
                  <Authenticator.SignUp.FormFields />

                  <div className="amplify-field">
                    <label className="amplify-label">First Name</label>
                    <input
                      name="given_name"
                      type="text"
                      required
                      className="amplify-input"
                    />
                  </div>

                  <div className="amplify-field">
                    <label className="amplify-label">Last Name</label>
                    <input
                      name="family_name"
                      type="text"
                      required
                      className="amplify-input"
                    />
                  </div>

                  <div className="amplify-field">
                    <label className="amplify-label">Birthday</label>
                    <input
                      name="birthdate"
                      type="date"
                      required
                      className="amplify-input"
                    />
                  </div>

                  {accountType === "Organization" && (
                    <>
                      <div className="amplify-field">
                        <label className="amplify-label">Business Name</label>
                        <input
                          name="custom:BusinessName"
                          type="text"
                          required
                          className="amplify-input"
                        />
                      </div>
                      <div className="amplify-field">
                        <label className="amplify-label">Business EIN</label>
                        <input
                          name="custom:BusinessEIN"
                          type="text"
                          required
                          className="amplify-input"
                        />
                      </div>
                      <div className="amplify-field">
                        <label className="amplify-label">Country</label>
                        <input
                          name="custom:Country"
                          type="text"
                          required
                          className="amplify-input"
                        />
                      </div>
                      <div className="amplify-field">
                        <label className="amplify-label">Address</label>
                        <input
                          name="address"
                          type="text"
                          required
                          className="amplify-input"
                        />
                      </div>
                      <div className="amplify-field">
                        <label className="amplify-label">City</label>
                        <input
                          name="custom:City"
                          type="text"
                          required
                          className="amplify-input"
                        />
                      </div>
                      <div className="amplify-field">
                        <label className="amplify-label">State</label>
                        <select
                          type="text"
                          name="custom:State"
                          style={{
                            width: "100%",
                            padding: 10,
                            fontSize: 15,
                            borderRadius: 8,
                            border: "1.5px solid #ccc",
                            boxSizing: "border-box",
                          }}
                        >
                          <option value="">Select a State</option>
                          {states.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="amplify-field">
                        <label className="amplify-label">Zip Code</label>
                        <input
                          name="custom:ZipCode"
                          type="text"
                          required
                          className="amplify-input"
                        />
                      </div>
                      <div className="amplify-field">
                        <label className="amplify-label">Phone Number</label>
                        <input
                          name="custom:PhoneNumber"
                          type="text"
                          required
                          className="amplify-input"
                        />
                      </div>
                      <div className="amplify-field">
                        <label className="amplify-label">Website</label>
                        <input
                          name="website"
                          type="url"
                          placeholder="https://example.com"
                          className="amplify-input"
                        />
                      </div>
                    </>
                  )}

                  <p style={{ marginTop: 16 }}>
                    A digital health platform that enables users to receive
                    immediate results from evidence-based assessments, create
                    personalized health plans, track meaningful progress,
                    collaborate with health professionals, and access to
                    CoConnect™ social platform that encourages healthy habits.
                  </p>
                  <p style={{ marginTop: 16, marginBottom: 0 }}>
                    You will need an authenticator app to complete your account
                    setup for protection. Some authenticator apps include
                    Google, Duo, and Authy. Keep your authenticator app because
                    you will need the code it provides for each sign in.{" "}
                    <strong>
                      If you lose access to your authenticator, you will need to
                      create a new account.
                    </strong>
                  </p>
                </>
              );
            },
          },
        }}
      >
        {({ signOut, user }) => (
          <main style={{ textAlign: "center" }}>
            <SidebarWithContent />
            <TOTPSetup user={user} />
            {showPopup && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 9999,
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    padding: 30,
                    borderRadius: 12,
                    width: 400,
                    maxWidth: "90%",
                    textAlign: "center",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                  }}
                >
                  <h2 style={{ marginBottom: 20 }}>Are you still there?</h2>
                  <p style={{ marginBottom: 30 }}>
                    For security, you will be logged out soon due to inactivity.
                  </p>
                  <button
                    onClick={handleStillHere}
                    style={{
                      padding: "10px 20px",
                      fontSize: 16,
                      borderRadius: 8,
                      border: "none",
                      backgroundColor: "#0067B3",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    Yes, I’m here
                  </button>
                </div>
              </div>
            )}
          </main>
        )}
      </Authenticator>
    </div>
  );
}
